/**
 * M07FinancialSummary - Banner "Tổng kết kế hoạch tài chính"
 * Pattern: matches Curex 8_cash_flow_illustration/05_full_page.png
 *
 * Layout: full-width banner với gradient xanh đậm, hiển thị 3 metric lớn:
 * 1. Tổng phí bảo hiểm chính (20 năm)
 * 2. Nhận về (dự kiến) năm thứ 20 - tỷ suất thấp 0.5%
 * 3. Nhận về (dự kiến) năm thứ 20 - tỷ suất cao 4.0%
 *
 * + Subtitle "Giá trị dự kiến nhận lại tại năm thứ 20..."
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export interface M07FinancialSummaryProps {
  basePremium: number;
  sumAssured: string;
  surrender20LowText: string;
  surrender20HighText: string;
  term: string;
}

export const M07FinancialSummary: React.FC<M07FinancialSummaryProps> = ({
  basePremium,
  surrender20LowText,
  surrender20HighText,
}) => {
  const totalPremium20Years = basePremium * 20;

  return (
    <View style={styles.banner}>
      {/* Header */}
      <View style={styles.headerRow}>
        <View style={styles.titleWrap}>
          <View style={styles.iconCircle}>
            <Text style={styles.iconCircleText}>$</Text>
          </View>
          <Text style={styles.title}>Tổng kết kế hoạch tài chính</Text>
        </View>
      </View>
      <Text style={styles.subtitle}>
        Giá trị dự kiến nhận lại tại năm thứ 20. Kế hoạch này giúp khách hàng bảo vệ tài chính trước mọi rủi ro sức khoẻ.
      </Text>

      {/* 3 metrics grid */}
      <View style={styles.metricsGrid}>
        <View style={styles.metricBox}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>Tổng phí bảo hiểm chính</Text>
          </View>
          <Text style={styles.metricValue}>+{totalPremium20Years.toLocaleString('vi-VN')}</Text>
        </View>

        <View style={styles.metricBox}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel} numberOfLines={1}>Nhận về (dự kiến) năm thứ 20</Text>
            <View style={styles.rateBadge}>
              <Text style={styles.rateBadgeText}>Tỷ suất thấp 0.5%</Text>
              <Icon name="trending_down" size={10} color="#92400E" />
            </View>
          </View>
          <Text style={styles.metricValue}>+{surrender20LowText.replace(/ đ/g, '')}</Text>
        </View>

        <View style={styles.metricBox}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel} numberOfLines={1}>Nhận về (dự kiến) năm thứ 20</Text>
            <View style={styles.rateBadge}>
              <Text style={styles.rateBadgeText}>Tỷ suất cao 4.0%</Text>
              <Icon name="trending_up" size={10} color="#92400E" />
            </View>
          </View>
          <Text style={styles.metricValue}>+{surrender20HighText.replace(/ đ/g, '')}</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#00A651',
    borderRadius: borderRadius.md,
    padding: spacing[5],
    overflow: 'hidden',
    position: 'relative',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[2],
  },
  titleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconCircleText: {
    fontSize: 18,
    fontWeight: '900',
    color: '#ffffff',
  },
  title: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  subtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
    marginBottom: spacing[4],
    lineHeight: 18,
  },
  metricsGrid: {
    flexDirection: 'row',
    gap: spacing[3],
    flexWrap: 'wrap',
  },
  metricBox: {
    flex: 1,
    minWidth: 140,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[3],
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing[1],
    marginBottom: spacing[2],
  },
  metricLabel: {
    flex: 1,
    fontSize: 11,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: fontWeights.bold,
    color: '#003366',
    letterSpacing: -0.3,
  },
  rateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 9999,
    backgroundColor: '#FEF3C7',
  },
  rateBadgeText: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    color: '#92400E',
  },
});